import { useMemo } from "react";
import { fmtEUR } from "@/lib/format";
import { COEFFICIENTI, listBeni, findAliquota } from "@/lib/coefficienti";
import { Plus, Trash2, AlertTriangle, CheckCircle2, Info } from "lucide-react";

const DEFAULT_GRUPPO = COEFFICIENTI[0].gruppo;
const DEFAULT_BENE = COEFFICIENTI[0].beni[0];

export default function BeniBreakdown({ rows, setRows, investimentoTotale }) {
  const totale = useMemo(
    () => rows.reduce((s, r) => s + (parseFloat(r.importo) || 0), 0),
    [rows]
  );
  const inv = parseFloat(investimentoTotale) || 0;
  const overflow = totale > inv + 0.5;
  const remaining = inv - totale;
  const aliquotaWeighted = useMemo(() => {
    if (totale <= 0) return 0;
    const num = rows.reduce(
      (s, r) => s + (parseFloat(r.importo) || 0) * (parseFloat(r.aliquota) || 0),
      0
    );
    return num / totale;
  }, [rows, totale]);

  const addRow = () => {
    setRows([
      ...rows,
      {
        gruppo: DEFAULT_GRUPPO,
        descrizione: DEFAULT_BENE.descrizione,
        aliquota: DEFAULT_BENE.aliquota,
        importo: "",
      },
    ]);
  };

  const updateRow = (idx, patch) => {
    setRows(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const removeRow = (idx) => setRows(rows.filter((_, i) => i !== idx));

  const onGruppoChange = (idx, gruppo) => {
    const beni = listBeni(gruppo);
    const first = beni[0] || {};
    updateRow(idx, {
      gruppo,
      descrizione: first.descrizione || "",
      aliquota: first.aliquota || 12.5,
    });
  };

  const onBeneChange = (idx, descrizione) => {
    const row = rows[idx];
    const aliquota = findAliquota(row.gruppo, descrizione);
    updateRow(idx, { descrizione, aliquota: aliquota ?? row.aliquota });
  };

  return (
    <div className="mt-6 bg-white border border-line rounded-lg shadow-card overflow-hidden">
      <div className="px-5 py-4 border-b border-line flex items-center justify-between gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Dettaglio</div>
          <h3 className="font-display text-lg text-ink">Investimento per bene</h3>
        </div>
        <button
          onClick={addRow}
          data-testid="beni-add"
          className="inline-flex items-center gap-2 h-10 px-4 bg-brand text-white rounded-md text-sm font-medium hover:bg-brand-dark"
        >
          <Plus className="w-4 h-4" /> Aggiungi bene
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="p-10 text-center">
          <Info className="w-8 h-8 mx-auto text-ink-light" />
          <p className="mt-3 text-sm text-ink-muted">
            Aggiungi le voci di investimento per ottenere un ammortamento composto sulla base delle
            aliquote specifiche di ogni bene.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-bg-dark text-white">
              <tr>
                <th className="text-left px-3 py-3 font-display text-[10px] tracking-widest uppercase">Gruppo</th>
                <th className="text-left px-3 py-3 font-display text-[10px] tracking-widest uppercase">Descrizione</th>
                <th className="text-right px-3 py-3 font-display text-[10px] tracking-widest uppercase w-32">
                  Coefficiente<br /><span className="text-brand normal-case">ammortamento</span>
                </th>
                <th className="text-right px-3 py-3 font-display text-[10px] tracking-widest uppercase w-20">
                  Anni<br /><span className="text-brand normal-case">amm.to</span>
                </th>
                <th className="text-right px-3 py-3 font-display text-[10px] tracking-widest uppercase w-40">Importo €</th>
                <th className="px-2 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {rows.map((r, idx) => {
                // Backward compat con vecchie righe (gruppo/specie o key/label)
                const gruppo = r.gruppo && COEFFICIENTI.find((g) => g.gruppo === r.gruppo) ? r.gruppo : DEFAULT_GRUPPO;
                const beniOpts = listBeni(gruppo);
                return (
                  <tr key={idx} data-testid={`beni-row-${idx}`}>
                    <td className="px-3 py-2 min-w-[180px]">
                      <select
                        value={gruppo}
                        onChange={(e) => onGruppoChange(idx, e.target.value)}
                        data-testid={`beni-gruppo-${idx}`}
                        className="w-full h-9 px-2 bg-white border border-line rounded text-xs focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                      >
                        {COEFFICIENTI.map((g) => (
                          <option key={g.gruppo} value={g.gruppo}>{g.gruppo}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2 min-w-[240px]">
                      <select
                        value={r.descrizione}
                        onChange={(e) => onBeneChange(idx, e.target.value)}
                        data-testid={`beni-desc-${idx}`}
                        className="w-full h-9 px-2 bg-white border border-line rounded text-xs focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                      >
                        {beniOpts.map((b) => (
                          <option key={b.descrizione} value={b.descrizione}>{b.descrizione}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <div className="relative">
                        <input
                          type="number"
                          value={r.aliquota}
                          onChange={(e) => updateRow(idx, { aliquota: e.target.value })}
                          data-testid={`beni-aliq-${idx}`}
                          step="0.5"
                          min="0"
                          max="100"
                          className="w-full h-9 pl-2 pr-6 bg-white border border-brand/50 rounded text-sm text-right font-mono focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-ink-muted">%</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div
                        data-testid={`beni-anni-${idx}`}
                        className="font-mono text-sm text-ink"
                        title="Durata stimata (regola del mezzo esercizio nel primo anno)"
                      >
                        {(() => {
                          const a = parseFloat(r.aliquota) || 0;
                          if (a <= 0) return "—";
                          const anni = Math.ceil(100 / a + 0.5);
                          return `${anni} anni`;
                        })()}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="relative">
                        <input
                          type="number"
                          value={r.importo}
                          onChange={(e) => updateRow(idx, { importo: e.target.value })}
                          data-testid={`beni-imp-${idx}`}
                          step="100"
                          min="0"
                          placeholder="0"
                          className="w-full h-9 pl-2 pr-5 bg-white border border-line rounded text-sm text-right font-mono focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-ink-muted">€</span>
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      <button
                        onClick={() => removeRow(idx)}
                        data-testid={`beni-remove-${idx}`}
                        title="Rimuovi riga"
                        className="p-1.5 text-ink-muted hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-bg border-t-2 border-line">
                <td colSpan={2} className="px-3 py-3 text-right font-display uppercase tracking-widest text-[11px] text-ink-muted">
                  Aliquota media ponderata (calcolo)
                </td>
                <td className="px-3 py-3 text-right font-mono text-brand font-semibold">
                  {aliquotaWeighted.toFixed(2)}%
                </td>
                <td></td>
                <td className="px-3 py-3 text-right font-mono font-semibold text-ink">
                  {fmtEUR(totale)}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {rows.length > 0 && (
        <div className={`px-5 py-3 border-t border-line flex items-center gap-2 text-sm ${overflow ? "bg-red-50 text-red-700" : totale === inv ? "bg-brand/[0.08] text-brand" : "bg-bg text-ink-muted"}`} data-testid="beni-summary">
          {overflow ? (
            <>
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>
                La somma dei beni <strong>({fmtEUR(totale)})</strong> supera l'investimento totale <strong>({fmtEUR(inv)})</strong> di <strong>{fmtEUR(totale - inv)}</strong>. Riduci gli importi o aumenta l'investimento.
              </span>
            </>
          ) : totale === 0 ? (
            <>
              <Info className="w-4 h-4 flex-shrink-0" />
              <span>Inserisci gli importi per i beni per attivare il calcolo composto.</span>
            </>
          ) : totale === inv ? (
            <>
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
              <span>Ripartizione completa: <strong>{fmtEUR(totale)}</strong> = investimento totale.</span>
            </>
          ) : (
            <>
              <Info className="w-4 h-4 flex-shrink-0" />
              <span>
                Assegnati <strong>{fmtEUR(totale)}</strong> di <strong>{fmtEUR(inv)}</strong> — restano <strong>{fmtEUR(remaining)}</strong> non attribuiti (calcolati con aliquota base).
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
}
